<template>
  <div :class="[isShort ? 'item short' : 'item', 'without-image', `card-${categorySlug}`]" class="post-card" :style="cardStyle">
    <div class="content-wrapper">
      <h3 :style="{ color: useBlackText ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 1)' }">{{ truncateWords(stripMarkdown(decodeHtml(post.title)), 8) }}</h3>
      <el-tag v-if="post.category" type="info" effect="dark" size="small">
        <b>{{ post.category.name }}</b>
      </el-tag>
      <br clear="all" />
      <p class="post_date" :style="{ color: useBlackText ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)' }">{{ timeAgo(post.created_at) }}</p>
      <p class="excerpt" :style="{ color: useBlackText ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)' }">{{ truncateWords(stripMarkdown(decodeHtml(post.excerpt)), 18) }}</p>
      <a :href="`/post/${post.slug}/${post.id}`" class="read-more" :style="{ color: useBlackText ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)' }">
        {{ $t('buttons.read_more') }}
      </a>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { decodeHtml } from '@/utils/string.js';
import { timeAgo } from '@/utils/date'

export default {
  name: 'PostCard',
  props: {
    post: {
      type: Object,
      required: true,
    },
    isShort: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t } = useI18n();

    return {
      timeAgo,
      decodeHtml,
      t,
    };
  },
  computed: {
    categorySlug() {
      if (!this.post.category) return 'default';
      return this.post.category.slug || 'default';
    },
    // Generate intensity level (0-100) based on view count/popularity
    // More views = more intense/saturated colors
    colorIntensity() {
      const viewCount = this.post.view_count || 0;
      
      // Using logarithmic scale for better distribution
      // Posts with 0 views get lightest colors (intensity ~15)
      // Very popular posts (10k+ views) get most intense colors (intensity ~95)
      
      if (viewCount === 0) {
        return 15; // Lightest for new posts
      }
      
      // Logarithmic scale: more views = more intense
      // log10(views + 1) / log10(10001) maps 0-10000 views to 0-1
      const maxViews = 10000;
      const logViews = Math.log10(viewCount + 1);
      const logMax = Math.log10(maxViews + 1);
      const normalized = logViews / logMax;
      
      // Map to intensity range 15-95 (leaving some room at extremes)
      const intensity = 15 + (normalized * 80);
      
      return Math.min(95, Math.max(15, Math.round(intensity)));
    },
    // Generate color variation style
    cardStyle() {
      return this.getCategoryColorStyle(this.categorySlug, this.colorIntensity);
    },
    // Determine if text should be black (for light backgrounds)
    useBlackText() {
      // Use black text for lighter backgrounds (intensity < 60)
      return this.colorIntensity < 60;
    },
  },
  methods: {
    getCategoryColorStyle(categorySlug, intensity) {
      // Normalize intensity to 0-1 range
      const normalizedIntensity = intensity / 100;
      
      // Color palettes for each category (light to dark spectrum)
      const palettes = {
        'ai': [
          { bg: ['#f9fafb', '#f3f4f6'], text: '#000000' },
          { bg: ['#f3f4f6', '#e5e7eb'], text: '#000000' },
          { bg: ['#e5e7eb', '#d1d5db'], text: '#000000' },
          { bg: ['#d1d5db', '#9ca3af'], text: '#000000' },
          { bg: ['#9ca3af', '#6b7280'], text: '#ffffff' },
        ],
        'machine-learning': [
          { bg: ['#fafafa', '#f5f5f5'], text: '#000000' },
          { bg: ['#f5f5f5', '#e5e5e5'], text: '#000000' },
          { bg: ['#e5e5e5', '#d4d4d4'], text: '#000000' },
          { bg: ['#d4d4d4', '#a3a3a3'], text: '#000000' },
          { bg: ['#a3a3a3', '#737373'], text: '#ffffff' },
        ],
        'cybersecurity': [
          { bg: ['#fafafa', '#f4f4f5'], text: '#000000' },
          { bg: ['#f4f4f5', '#e4e4e7'], text: '#000000' },
          { bg: ['#e4e4e7', '#d4d4d8'], text: '#000000' },
          { bg: ['#d4d4d8', '#a1a1aa'], text: '#000000' },
          { bg: ['#a1a1aa', '#71717a'], text: '#ffffff' },
        ],
        'data-science': [
          { bg: ['#F5F1E8', '#E8DCC6'], text: '#000000' },
          { bg: ['#E8DCC6', '#D4CEC5'], text: '#000000' },
          { bg: ['#D4CEC5', '#B8A99A'], text: '#000000' },
          { bg: ['#B8A99A', '#9A8B7A'], text: '#000000' },
          { bg: ['#9A8B7A', '#7A6B5A'], text: '#ffffff' },
        ],
        'blockchain': [
          { bg: ['#fffbeb', '#fef3c7'], text: '#000000' },
          { bg: ['#fef3c7', '#fde68a'], text: '#000000' },
          { bg: ['#fde68a', '#fcd34d'], text: '#78350f' },
          { bg: ['#fcd34d', '#fbbf24'], text: '#78350f' },
          { bg: ['#fbbf24', '#f59e0b'], text: '#ffffff' },
        ],
        'quantum-computing': [
          { bg: ['#faf5ff', '#f3e8ff'], text: '#4c1d95' },
          { bg: ['#f3e8ff', '#e9d5ff'], text: '#4c1d95' },
          { bg: ['#e9d5ff', '#ddd6fe'], text: '#5b21b6' },
          { bg: ['#ddd6fe', '#c4b5fd'], text: '#5b21b6' },
          { bg: ['#c4b5fd', '#a78bfa'], text: '#ffffff' },
        ],
        'internet-of-things-iot': [
          { bg: ['#D8D4C0', '#C8C4B0'], text: '#000000' },
          { bg: ['#C8C4B0', '#B8B4A0'], text: '#000000' },
          { bg: ['#B8B4A0', '#9A9680'], text: '#000000' },
          { bg: ['#9A9680', '#7A8B7A'], text: '#000000' },
          { bg: ['#7A8B7A', '#5A6B5A'], text: '#ffffff' },
        ],
        'iot': [
          { bg: ['#D8D4C0', '#C8C4B0'], text: '#000000' },
          { bg: ['#C8C4B0', '#B8B4A0'], text: '#000000' },
          { bg: ['#B8B4A0', '#9A9680'], text: '#000000' },
          { bg: ['#9A9680', '#7A8B7A'], text: '#000000' },
          { bg: ['#7A8B7A', '#5A6B5A'], text: '#ffffff' },
        ],
        'ar-vr': [
          { bg: ['#F5F1E8', '#E8D4D0'], text: '#000000' },
          { bg: ['#E8D4D0', '#D8C4C0'], text: '#000000' },
          { bg: ['#D8C4C0', '#C8B4B0'], text: '#000000' },
          { bg: ['#C8B4B0', '#B8A4A0'], text: '#000000' },
          { bg: ['#B8A4A0', '#9A8A80'], text: '#ffffff' },
        ],
        'ar--vr': [
          { bg: ['#F5F1E8', '#E8D4D0'], text: '#000000' },
          { bg: ['#E8D4D0', '#D8C4C0'], text: '#000000' },
          { bg: ['#D8C4C0', '#C8B4B0'], text: '#000000' },
          { bg: ['#C8B4B0', '#B8A4A0'], text: '#000000' },
          { bg: ['#B8A4A0', '#9A8A80'], text: '#ffffff' },
        ],
        'autonomous-vehicles': [
          { bg: ['#D4D8C5', '#C4C8B5'], text: '#000000' },
          { bg: ['#C4C8B5', '#B4B8A5'], text: '#000000' },
          { bg: ['#B4B8A5', '#9A9E8B'], text: '#000000' },
          { bg: ['#9A9E8B', '#7A8B7A'], text: '#000000' },
          { bg: ['#7A8B7A', '#5A6B5A'], text: '#ffffff' },
        ],
        'space-technology': [
          { bg: ['#F5F1E8', '#E8DCC6'], text: '#000000' },
          { bg: ['#E8DCC6', '#D4CEC5'], text: '#000000' },
          { bg: ['#D4CEC5', '#B8A99A'], text: '#000000' },
          { bg: ['#B8A99A', '#9A8B7A'], text: '#000000' },
          { bg: ['#9A8B7A', '#7A6B5A'], text: '#ffffff' },
        ],
        'renewable-energy': [
          { bg: ['#D8D4C0', '#C8C4B0'], text: '#000000' },
          { bg: ['#C8C4B0', '#B8B4A0'], text: '#000000' },
          { bg: ['#B8B4A0', '#9A9680'], text: '#000000' },
          { bg: ['#9A9680', '#7A8B7A'], text: '#000000' },
          { bg: ['#7A8B7A', '#5A6B5A'], text: '#ffffff' },
        ],
        'biotechnology': [
          { bg: ['#D4D8C5', '#C4C8B5'], text: '#000000' },
          { bg: ['#C4C8B5', '#B4B8A5'], text: '#000000' },
          { bg: ['#B4B8A5', '#9A9E8B'], text: '#000000' },
          { bg: ['#9A9E8B', '#7A8B7A'], text: '#000000' },
          { bg: ['#7A8B7A', '#5A6B5A'], text: '#ffffff' },
        ],
        'tech-conferences': [
          { bg: ['#eef2ff', '#e0e7ff'], text: '#1e293b' },
          { bg: ['#e0e7ff', '#c7d2fe'], text: '#1e293b' },
          { bg: ['#c7d2fe', '#a5b4fc'], text: '#312e81' },
          { bg: ['#a5b4fc', '#818cf8'], text: '#312e81' },
          { bg: ['#818cf8', '#6366f1'], text: '#ffffff' },
        ],
        'hackathons': [
          { bg: ['#f5f3ff', '#ede9fe'], text: '#4c1d95' },
          { bg: ['#ede9fe', '#ddd6fe'], text: '#4c1d95' },
          { bg: ['#ddd6fe', '#c4b5fd'], text: '#5b21b6' },
          { bg: ['#c4b5fd', '#a78bfa'], text: '#5b21b6' },
          { bg: ['#a78bfa', '#8b5cf6'], text: '#ffffff' },
        ],
        'tech-summits': [
          { bg: ['#F5F1E8', '#E8DCC6'], text: '#000000' },
          { bg: ['#E8DCC6', '#D4CEC5'], text: '#000000' },
          { bg: ['#D4CEC5', '#B8A99A'], text: '#000000' },
          { bg: ['#B8A99A', '#9A8B7A'], text: '#000000' },
          { bg: ['#9A8B7A', '#7A6B5A'], text: '#ffffff' },
        ],
        'startups': [
          { bg: ['#fffbeb', '#fef3c7'], text: '#000000' },
          { bg: ['#fef3c7', '#fde68a'], text: '#000000' },
          { bg: ['#fde68a', '#fcd34d'], text: '#78350f' },
          { bg: ['#fcd34d', '#fbbf24'], text: '#78350f' },
          { bg: ['#fbbf24', '#f59e0b'], text: '#ffffff' },
        ],
        'entrepreneurship': [
          { bg: ['#D8D4C0', '#C8C4B0'], text: '#000000' },
          { bg: ['#C8C4B0', '#B8B4A0'], text: '#000000' },
          { bg: ['#B8B4A0', '#9A9680'], text: '#000000' },
          { bg: ['#9A9680', '#7A8B7A'], text: '#000000' },
          { bg: ['#7A8B7A', '#5A6B5A'], text: '#ffffff' },
        ],
        'default': [
          { bg: ['#f9fafb', '#f3f4f6'], text: '#000000' },
          { bg: ['#f3f4f6', '#e5e7eb'], text: '#000000' },
          { bg: ['#e5e7eb', '#d1d5db'], text: '#000000' },
          { bg: ['#d1d5db', '#9ca3af'], text: '#000000' },
          { bg: ['#9ca3af', '#6b7280'], text: '#ffffff' },
        ],
      };
      
      const palette = palettes[categorySlug] || palettes['default'];
      // Select intensity level (0-4) based on normalized intensity
      const level = Math.floor(normalizedIntensity * (palette.length - 1));
      const color = palette[level];
      
      return {
        background: `linear-gradient(135deg, ${color.bg[0]} 0%, ${color.bg[1]} 100%)`,
        // Color is set inline on individual elements based on useBlackText
      };
    },
    navigateToPost() {
      window.location.href = `/post/${this.post.slug}/${this.post.id}`;
    },
    truncateWords(text, wordLimit) {
      if (!text) return ''; // Handle null/undefined
      const words = text.split(' '); // Split into words
      if (words.length <= wordLimit) return text; // Return full text if within limit
      
      return words.slice(0, wordLimit).join(' ') + '...'; // Join words back and append "..."
    },
    stripMarkdown(text) {
      return text
        .replace(/[_*~`]/g, '')  // Remove _, *, ~, ` characters
        .replace(/\[.*?\]\(.*?\)/g, '')  // Remove links like [text](url)
        .replace(/#{1,6}\s?/g, '')  // Remove Markdown headings
        .replace(/!\[.*?\]\(.*?\)/g, '')  // Remove images ![alt](url)
        .replace(/>\s?/g, '')  // Remove blockquotes
        .replace(/-{3,}/g, '')  // Remove horizontal rules (---)
        .replace(/\*\*(.*?)\*\*/g, '$1')  // Remove bold **text**
        .replace(/\*(.*?)\*/g, '$1');  // Remove italic *text*
    },
  },
};
</script>


<style scoped>
.post-card {
  text-align: left;
  margin-left: 20px;
  margin-bottom: 40px;
  box-sizing: border-box;
  width: 350px;
  overflow: hidden;
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.post-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Category-specific colored backgrounds */
.post-card {
  background: #f8f9fa;
}

/* Colors are now applied dynamically via inline styles based on post ID */

.content-wrapper {
  padding: 30px;
}

h3 {
  padding: 0;
  margin-top: 10px;
  color: inherit;
}

.excerpt {
  font-size: 15px;
}

.read-more {
  font-size: 15px;
  font-weight: 500;
}

.post_date {
  font-size: 14px;
}

.item.short {
  height: auto;
  min-height: 250px;
}

.item {
  height: auto;
  min-height: 400px;
}

.item.without-image {
  height: auto;
  min-height: 350px;
}

@media (max-width: 768px) {
  .post-card {
    width: 100% !important;
    margin-left: 0 !important;
    margin-bottom: 30px !important;
  }

  .content-wrapper {
    padding: 25px !important;
  }
}
</style>
